import React, { useState } from 'react'
import NoteCard from './NoteCard'
import type { NoteImageRecord, NoteTag, WorkspaceNote } from './notesWorkspace.types'

interface NoteThreadProps {
	note: WorkspaceNote
	replies: WorkspaceNote[]
	imageRecords: NoteImageRecord[]
	currentUserId: string
	canReply: boolean
	/** Shown top-right on the parent card. Replies inherit it, so they omit it. */
	tag?: NoteTag | null
	onReply: (parentId: string) => void
	onUpdateImage?: (image: NoteImageRecord) => void | Promise<void>
	onRemoveImage?: (args: {
		image: NoteImageRecord
		url: string
		noteId?: string
		noteBody: string
	}) => void | Promise<void>
	/**
	 * Asked per note rather than passed as a boolean, because a thread renders
	 * several notes and the answer differs between them: the parent may be the
	 * viewer's own while a reply is somebody else's.
	 */
	canDeleteNote?: (note: WorkspaceNote) => boolean
	onDeleteNote?: (note: WorkspaceNote) => void
	/** "Make tasks" from a note (MYD-643); absent, no control. */
	onMakeTasks?: (note: WorkspaceNote) => void
	/** Set when the rail asked to open a specific image inside this note. */
	openImageIndex?: number | null
	onImageReviewClose?: () => void
}

/** Longer threads start collapsed so a busy feed stays scannable. */
const AUTO_EXPAND_LIMIT = 3

/**
 * A note together with its replies.
 *
 * The old feed passed replies in as children and rendered them flat: no indent,
 * no thread line, no count, so a reply was indistinguishable from a new note.
 * Here they sit behind a rule, indented, under a count that toggles them.
 */
const NoteThread: React.FC<NoteThreadProps> = ({
	note,
	replies,
	imageRecords,
	currentUserId,
	tag = null,
	canReply,
	onReply,
	onUpdateImage,
	onRemoveImage,
	canDeleteNote,
	onDeleteNote,
	onMakeTasks,
	openImageIndex = null,
	onImageReviewClose,
}) => {
	const [expanded, setExpanded] = useState(replies.length <= AUTO_EXPAND_LIMIT)

	return (
		<div data-testid="note-thread">
			<NoteCard
				note={note}
				imageRecords={imageRecords}
				currentUserId={currentUserId}
				canReply={canReply}
				tag={tag}
				replyCount={replies.length}
				onReply={onReply}
				onToggleReplies={() => setExpanded(current => !current)}
				repliesExpanded={expanded}
				onUpdateImage={onUpdateImage}
				onRemoveImage={onRemoveImage}
				canDelete={canDeleteNote?.(note) ?? false}
				onDelete={onDeleteNote ? () => onDeleteNote(note) : undefined}
				onMakeTasks={onMakeTasks ? () => onMakeTasks(note) : undefined}
				openImageIndex={openImageIndex}
				onImageReviewClose={onImageReviewClose}
			/>

			{replies.length > 0 && expanded && (
				<div className="ml-5 mt-2 space-y-2 border-l-2 border-border pl-4 sm:ml-8">
					{replies.map(reply => (
						<NoteCard
							key={reply._id}
							note={reply}
							imageRecords={imageRecords}
							currentUserId={currentUserId}
							isReply
							canReply={false}
							onUpdateImage={onUpdateImage}
							onRemoveImage={onRemoveImage}
							canDelete={canDeleteNote?.(reply) ?? false}
							onDelete={onDeleteNote ? () => onDeleteNote(reply) : undefined}
							onMakeTasks={onMakeTasks ? () => onMakeTasks(reply) : undefined}
						/>
					))}
				</div>
			)}
		</div>
	)
}

export default NoteThread
